import TableSkeletonComponent from "@/app/components/ui/LoaderComponent";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import { useAppoitmentStatusList } from "@/app/hooks/api_hooks/dayClosing/useAppoitmentStatusList";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import { useUtils } from "@/app/hooks/useUtils";
import { useEffect, useState } from "react";
import ProductItemImage from "../../OrderManagement/ModalComponent/ProductItemImage";
import OrderStatus from "../../OrderManagement/OrderStatusButton";
import AcceptReturnItemButtonComponent from "../../OrderManagement/ModalComponent/AcceptReturnItemButtonComponent";
import Username from "@/app/components/ui/Reusable/Username";

const ReturnsClosureComponent = () => {
  const { execute: fetchDayClosingData, closureData } = useAppoitmentStatusList();
  const { veticDateFormat } = useUtils();
  const queryParams = new URLSearchParams(location.search);
  const isLoading = useLoader("isClosingDataInProgress");
  const { NO_APPOINTMENTS } = useImageConstants();
  const [open, setOpen] = useState(false);
  const [orders, setOrders] = useState([]);

  // ✅ read query params
  const processId = queryParams.get("closing_process") ?? "";
  const queryCity = queryParams.get("city_id");
  const queryClinics = queryParams.get("clinic_ids");
  const queryShift = queryParams.get("shift") ?? "day";
  const time = queryParams.get("t") ?? "";

  useEffect(() => {
    if (queryClinics && queryClinics) {
      fetchDayClosingData(queryClinics, queryShift, "operations", "returns");
    }
  }, [processId, queryCity, queryClinics, queryShift, time]);
  const clinicsWithReturns = closureData?.filter((clinic) => clinic?.closure_list?.length > 0) || [];

  return (
    <div className="w-full pb-4">
      {isLoading ? (
        <TableSkeletonComponent columnLength={9} />
      ) : clinicsWithReturns.length > 0 ? (
        <div className="overflow-y-auto max-h-[650px]">
          {clinicsWithReturns.map((clinic) => (
            <div
              key={clinic.clinic_details.clinic_id}
              className="mb-4 rounded-2xl border border-[#ECEEF2] bg-white p-3 shadow-sm"
            >
              <div className="overflow-x-auto rounded-xl">
                <table className="w-full min-w-[1120px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                  <thead className="bg-[#F5F5F5] text-[#1F1F23] text-xs font-normal">
                    <tr>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">Client Name</th>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">Order Details</th>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">Items</th>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">Status</th>
                      <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="text-xs">
                    {clinic.closure_list.map((order, rowIndex) => {
                      const isFirst = rowIndex === 0;
                      const isLast = rowIndex === clinic.closure_list.length - 1;
                      const isSingle = clinic.closure_list.length === 1;

                      return (
                      <tr
                        key={order.sub_order_id || order.invoice_id}
                        className={`
                          bg-white hover:bg-[#FCFCFD] transition-colors
                          ${isSingle ? "rounded-xl" : ""}
                          ${isFirst && !isSingle ? "rounded-t-xl" : ""}
                          ${isLast && !isSingle ? "rounded-b-xl" : ""}
                        `}
                      >
                        <td
                          className={`
                            p-3 text-xs text-blue-600
                            ${isSingle ? "rounded-tl-2xl rounded-bl-2xl" : ""}
                            ${isFirst && !isSingle ? "rounded-tl-2xl" : ""}
                            ${isLast && !isSingle ? "rounded-bl-2xl" : ""}
                          `}
                        >
                          <Username
                            username={order.user_details?.name}
                            petId={order.pet_details?.id}
                            userId={order.user_details?.id}
                          />
                        </td>
                        <td className="p-3 text-xs flex flex-col gap-1 whitespace-nowrap">
                          <span>{order?.order_id}</span>
                          <span>{veticDateFormat(order?.created_at)}</span>
                        </td>
                        <td className="p-3">
                          <ol>
                            {order?.sub_order_items?.map((item, id) => {
                              return (
                                <li key={id} className="flex">
                                  <div className="flex whitespace-break-spaces cursor-pointer text-xs">
                                    {id + 1}.
                                    <span>
                                      <ProductItemImage value={item} data={order.sub_order_items} />
                                    </span>
                                  </div>
                                </li>
                              );
                            })}
                          </ol>
                          {/* )} */}
                        </td>
                        <td className="p-3 text-xs whitespace-nowrap">
                          <OrderStatus status={order?.internal_order_status} />{" "}
                        </td>

                        <td
                          className={`
                            p-3 w-[190px]
                            ${isSingle ? "rounded-tr-2xl rounded-br-2xl" : ""}
                            ${isFirst && !isSingle ? "rounded-tr-2xl" : ""}
                            ${isLast && !isSingle ? "rounded-br-2xl" : ""}
                          `}
                        >
                          {[
                            "undelivered_in_transit",
                            "delivered",
                            "picked",
                            "undelivered",
                            "return_in_transit",
                          ]?.includes(order?.internal_order_status) &&
                            !order?.is_sub_order_returned && (
                              <button
                                onClick={() => {
                                  setOpen(true);
                                  setOrders(order);
                                }}
                                className="bg-black text-white px-4 py-2 rounded-lg text-xs font-medium w-full whitespace-nowrap hover:opacity-90 transition-opacity"
                              >
                                Accept Returned Items
                              </button>
                          )}
                        </td>
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <NoDataFoundComponent imageURL={NO_APPOINTMENTS} message="No Return Orders Found" />
      )}
      {open && <AcceptReturnItemButtonComponent open={open} setOpen={setOpen} orders={orders} isDayClosing={true} />}
    </div>
  );
};

export default ReturnsClosureComponent;
